(() => {
  if (customElements.get('thb-collection-v2')) return;
  class CollectionV2 extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.setAttribute('data-enhanced', '');
      this.loading = false;
      const {signal} = this.abort;
      this.panel = this.querySelector('[data-plp-filter-panel]');
      this.toggle = this.querySelector('[data-plp-filter-toggle]');
      this.catalog = this.querySelector('[data-plp-catalog]');
      this.form = this.querySelector('[data-plp-filter-form]');
      this.desktop = matchMedia('(min-width: 990px)');
      if (this.panel && this.toggle) {
        this.toggle.hidden = false;
        this.panel.addEventListener('toggle', () => this.syncPanel(), {signal});
        this.toggle.addEventListener('click', () => {this.panel.open = !this.panel.open; this.syncPanel();}, {signal});
        this.desktop.addEventListener('change', () => {
          const focusInside = this.panel.contains(document.activeElement);
          this.panel.open = this.desktop.matches;
          this.syncPanel();
          if (!this.panel.open && focusInside) this.toggle.focus();
        }, {signal});
        this.panel.open = this.desktop.matches;
        this.syncPanel();
      }
      this.querySelector('[data-plp-view-controls]').hidden = false;
      this.querySelector('[data-plp-sort-apply]').hidden = true;
      let view = 'grid';
      try { if (sessionStorage.getItem('thb:collection:view') === 'list') view = 'list'; } catch {}
      this.setView(view);
      this.sort = this.querySelector('[data-plp-sort]');
      this.appliedSort = this.sort.value;
      this.sort.addEventListener('change', () => {
        this.validateRange();
        if (!this.form.checkValidity()) {
          this.sort.value = this.appliedSort;
          return;
        }
        this.form.requestSubmit();
      }, {signal});
      this.form.addEventListener('invalid', event => {
        event.preventDefault();
        if (this.panel) {this.panel.open = true; this.syncPanel();}
        const facet = event.target.closest('details');
        if (facet) facet.open = true;
        const error = this.querySelector('[data-plp-filter-error]');
        if (error) {error.textContent = event.target.validationMessage; error.hidden = false;}
        event.target.setAttribute('aria-invalid', 'true');
        requestAnimationFrame(() => event.target.focus());
      }, {capture: true, signal});
      this.form.addEventListener('input', () => this.validateRange(), {signal});
      this.form.addEventListener('submit', event => {if (!this.validateRange()) {event.preventDefault(); this.form.reportValidity();}}, {signal});
      const stockSwitch = this.querySelector('[data-plp-stock-switch]');
      const available = this.form.querySelector('input[name="filter.v.availability"][value="1"]');
      const unavailable = this.form.querySelector('input[name="filter.v.availability"][value="0"]');
      if (stockSwitch && available) {
        const syncStock = () => {stockSwitch.setAttribute('aria-checked', String(available.checked && !unavailable?.checked)); stockSwitch.disabled = available.disabled && !available.checked;};
        stockSwitch.hidden = false;
        stockSwitch.addEventListener('click', () => {
          const enabled = stockSwitch.getAttribute('aria-checked') === 'true';
          available.checked = !enabled;
          if (!enabled && unavailable) unavailable.checked = false;
          syncStock(); this.form.requestSubmit();
        }, {signal});
        this.form.addEventListener('change', syncStock, {signal}); syncStock();
      }
      this.addEventListener('click', event => {
        const retry = event.target.closest('[data-plp-retry]');
        if (retry && !this.loading) {
          const link = this.querySelector('[data-plp-load]');
          if (link) this.loadMore(link);
          return;
        }
        const viewButton = event.target.closest('[data-plp-view]');
        if (viewButton && this.contains(viewButton)) this.setView(viewButton.dataset.plpView);
        const load = event.target.closest('[data-plp-load]');
        if (!load || !this.contains(load) || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (!this.loading) this.loadMore(load);
      }, {signal});
      this.querySelector('[data-plp-category-track]')?.addEventListener('focusin', event => {
        const link = event.target.closest('a'), track = this.querySelector('[data-plp-category-track]');
        if (!link || !track) return;
        const outer = track.getBoundingClientRect(), box = link.getBoundingClientRect();
        const delta = box.left < outer.left + 3 ? box.left - outer.left - 3 : box.right > outer.right - 3 ? box.right - outer.right + 3 : 0;
        if (delta) track.scrollBy({left: delta, behavior: 'instant'});
      }, {signal});
      this.validateRange();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.loading = false;
      this.querySelector('[data-plp-grid]')?.removeAttribute('aria-busy');
      this.querySelector('[data-plp-load]')?.removeAttribute('aria-disabled');
      const retry = this.querySelector('[data-plp-retry]');
      if (retry) retry.disabled = false;
    }
    syncPanel() {this.toggle.setAttribute('aria-expanded', String(this.panel.open)); this.catalog.toggleAttribute('data-filters-hidden', !this.panel.open);}
    setView(view) {
      const next = view === 'list' ? 'list' : 'grid';
      this.querySelector('[data-plp-grid]').dataset.view = next;
      this.querySelectorAll('[data-plp-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.plpView === next)));
      try {sessionStorage.setItem('thb:collection:view', next);} catch {}
    }
    validateRange() {
      let valid = true;
      this.querySelectorAll('[data-plp-price-range]').forEach(range => {
        const min = range.querySelector('[data-plp-min]'), max = range.querySelector('[data-plp-max]');
        const reversed = min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value);
        max.setCustomValidity(reversed ? this.dataset.rangeError : '');
        if (max.validity.valid) max.removeAttribute('aria-invalid');
        if (min.validity.valid) min.removeAttribute('aria-invalid');
        if (reversed) valid = false;
      });
      const error = this.querySelector('[data-plp-filter-error]');
      if (error && valid && !this.form.querySelector(':invalid')) {error.hidden = true; error.textContent = '';}
      return valid;
    }
    async loadMore(link) {
      const controller = this.abort;
      if (!controller || controller.signal.aborted) return;
      const {signal} = controller;
      const grid = this.querySelector('[data-plp-grid]'), panel = this.querySelector('[data-plp-pagination]'), status = this.querySelector('[data-plp-status]');
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      this.loading = true;
      const errorBox = this.querySelector('[data-plp-load-error]');
      const retry = errorBox?.querySelector('[data-plp-retry]');
      if (retry) retry.disabled = true;
      if (errorBox) errorBox.hidden = true;
      link.setAttribute('aria-disabled', 'true'); grid.setAttribute('aria-busy', 'true'); status.textContent = status.dataset.loading;
      try {
        const response = await fetch(url, {credentials: 'same-origin', signal, headers: {'Accept': 'text/html'}});
        if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Invalid collection response');
        if (new URL(response.url).pathname !== url.pathname) throw new Error('Collection redirect');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted || !this.isConnected) return;
        const nextRoot = [...document.querySelectorAll('thb-collection-v2')].find(root => root.dataset.sectionId === this.dataset.sectionId);
        const nextGrid = nextRoot?.querySelector('[data-plp-grid]'), nextPanel = nextRoot?.querySelector('[data-plp-pagination]');
        if (!nextGrid || !nextPanel || Number(nextPanel.dataset.page) !== Number(panel.dataset.page) + 1) throw new Error('Unexpected collection page');
        const seen = new Set([...grid.querySelectorAll('[data-commerce-card]')].map(card => card.dataset.productId));
        const cards = [...nextGrid.querySelectorAll(':scope > [data-commerce-card]')].filter(card => !seen.has(card.dataset.productId));
        if (!cards.length) throw new Error('No new products');
        const previous = panel.querySelector('[rel="prev"]'), fragment = document.createDocumentFragment();
        cards.forEach(card => fragment.append(card)); grid.append(fragment);
        const nextPrevious = nextPanel.querySelector('[rel="prev"]');
        if (previous) nextPrevious?.replaceWith(previous.cloneNode(true)); else nextPrevious?.remove();
        panel.replaceWith(nextPanel);
        const end = nextPanel.querySelector('[data-plp-end]');
        if (end && grid.querySelectorAll('[data-commerce-card]').length === Number(nextPanel.dataset.total)) end.textContent = end.dataset.complete;
        status.textContent = status.dataset.loaded.replace('__COUNT__', String(cards.length)).replace('__SHOWN__', String(grid.querySelectorAll('[data-commerce-card]').length));
        cards[0].querySelector('[data-commerce-product-link]')?.focus({preventScroll: true});
        cards[0].scrollIntoView({block: 'nearest', behavior: 'instant'});
      } catch (error) {
        if (error.name === 'AbortError' || signal.aborted) return;
        status.textContent = status.dataset.error;
        if (errorBox) {
          errorBox.querySelector('[data-plp-next-page]').href = link.href;
          errorBox.hidden = false;
        }
      } finally {
        if (this.abort === controller) {
          this.loading = false; link.removeAttribute('aria-disabled'); grid.removeAttribute('aria-busy'); if (retry) retry.disabled = false;
        }
      }
    }
  }
  customElements.define('thb-collection-v2', CollectionV2);
})();
